import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, SendIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type { ChangeRequest } from '../../types/selfService';
import { TODAY } from '../../utils/format';
import { hrActions } from '../../utils/hrStore';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { TextField } from '../ui/TextField';
export interface ChangeableField {
  section: ChangeRequest['section'];
  label: string;
  value: string;
}
interface ChangeRequestModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: string;
  fields: ChangeableField[];
  section: ChangeRequest['section'];
}
export function ChangeRequestModal({
  open,
  onClose,
  employeeId,
  fields,
  section
}: ChangeRequestModalProps) {
  const options = fields.filter(f => f.section === section);
  const [field, setField] = useState(options[0]?.label ?? '');
  const [requested, setRequested] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!open) return;
    setField(options[0]?.label ?? '');
    setRequested('');
    setNote('');
    setSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, section]);
  const current = options.find(f => f.label === field)?.value ?? '';
  const error = submitted && !requested.trim() ? 'Tell HR what it should say' : undefined;
  const submit = () => {
    setSubmitted(true);
    if (!requested.trim()) return;
    hrActions.addChangeRequest({
      id: `chg-${Date.now()}`,
      employeeId,
      section,
      field,
      current,
      requested: requested.trim(),
      note: note.trim(),
      submitted: format(TODAY, 'yyyy-MM-dd'),
      status: 'pending'
    });
    toast.success('Change request sent to HR', {
      description: `${field} — HR will review and update your record.`
    });
    onClose();
  };
  return <Modal open={open} onClose={onClose} title={`Request a change · ${section}`} description="Your record is read-only. HR reviews every change before it's applied." footer={<>
          <Button onClick={onClose} data-id="element-729" data-mp="24ju5xffzwesj">Cancel</Button>
          <Button variant="primary" icon={SendIcon} onClick={submit} data-id="element-730" data-mp="24l5dzrgh48pg">
            Send to HR
          </Button>
        </>} data-id="element-728" data-mp="2ehi5ciojqah1">
      <div className="space-y-4" data-id="element-731" data-mp="2x89mi6erz55z">
        <div data-id="element-732" data-mp="2x9aws8nyvnmn">
          <label htmlFor="change-field" className="block text-xs font-medium text-slate-600" data-id="element-733" data-mp="2x8hxttv922f2">
            What needs to change?
          </label>
          <div className="relative mt-1.5" data-id="element-734" data-mp="2nig8kyr5udyk">
            <select id="change-field" value={field} onChange={e => setField(e.target.value)} className="h-11 w-full appearance-none rounded-xl border-0 bg-white px-3.5 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-735" data-mp="2nheueleulbcm">
              {options.map(f => <option key={f.label} data-id="element-736" data-mp="19hjuwxywzdjn">{f.label}</option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-737" data-mp="14ka9ruik5o1m" />
          </div>
        </div>
        <div className="rounded-xl bg-canvas px-4 py-3" data-id="element-738" data-mp="1e9bmnbtt3pbd">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-739" data-mp="1e9trgwujbbuw">Currently on file</p>
          <p className="mt-0.5 text-sm text-navy" data-id="element-740" data-mp="qjziiqdc67fx">{current || '—'}</p>
        </div>
        <TextField label="Should be" value={requested} onChange={setRequested} placeholder="Enter the corrected value" error={error} data-id="element-741" data-mp="llxwbwkm5s08" />
        <div data-id="element-742" data-mp="zi2ryob1rdrd">
          <label htmlFor="change-note" className="block text-xs font-medium text-slate-600" data-id="element-743" data-mp="viu1bsbazw01">
            Note for HR <span className="text-muted" data-id="element-744" data-mp="2azavz8hs6rv0">(optional)</span>
          </label>
          <textarea id="change-note" rows={2} value={note} onChange={e => setNote(e.target.value)} className="mt-1.5 w-full resize-none rounded-xl border-0 px-3.5 py-2.5 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-745" data-mp="2b1f8getqwz6w" />
        </div>
      </div>
    </Modal>;
}